/* One page lock, with an independent owner for each open drawer. */
(() => {
  if (window.semboniaScrollLock) return;
  const owners = new Set();
  let saved;
  let restoring = false;
  let restoreFrame;
  const mobileBaseline = () => window.semboniaMobileBaseline?.active === true;
  const lockDisabled = () => window.semboniaMobileBaseline?.scrollLockDisabled === true;
  const capture = (node, names) => names.map(name => [name, node.style.getPropertyValue(name), node.style.getPropertyPriority(name)]);
  const restore = (node, entries) => entries.forEach(([name, value, priority]) => {
    if (value) node.style.setProperty(name, value, priority);
    else node.style.removeProperty(name);
  });
  function acquire(owner) {
    if (owners.has(owner)) return;
    // Temporary mobile isolation: retain overlay ownership for focus guards and
    // breakpoint changes, but do not change native scroll state or stop Lenis.
    if (lockDisabled()) {
      owners.add(owner);
      return;
    }
    cancelAnimationFrame(restoreFrame);
    restoring = false;
    if (!owners.size) {
      const body = document.body, root = document.documentElement;
      const lenis = window.semboniaLenis;
      // Native touch scrolling must keep the document in normal flow. Rapidly
      // fixing/unfixing body rebuilds Android Chrome's touch scrolling surface.
      const nativeTouch = window.matchMedia?.('(pointer: coarse)').matches === true;
      saved = {
        x:window.scrollX, y:window.scrollY, nativeTouch, lenis, resume:!!lenis && !lenis.isStopped,
        body:capture(body, ['position','top','left','width','overflow']),
        root:capture(root, ['overflow','scroll-behavior'])
      };
      // Publish the lock before fixed-body layout can emit scroll/resize events.
      owners.add(owner);
      window.dispatchEvent(new Event('sembonia:scroll-lock'));
      lenis?.stop();
      root.style.setProperty('scroll-behavior','auto','important');
      root.style.setProperty('overflow','hidden','important');
      const lockedBody = nativeTouch ? {overflow:'hidden'} :
        {position:'fixed',top:`-${saved.y}px`,left:`-${saved.x}px`,width:'100%',overflow:'hidden'};
      Object.entries(lockedBody).forEach(([name,value]) => body.style.setProperty(name,value,'important'));
    }
    owners.add(owner);
  }
  function release(owner) {
    if (!owners.delete(owner) || owners.size || !saved) return;
    restorePageLock();
  }
  function restorePageLock() {
    const state = saved;
    restoring = true;
    saved = null;
    restore(document.body, state.body);
    restore(document.documentElement, state.root.filter(([name]) => name !== 'scroll-behavior'));
    // Overflow-only locking retains the native position; avoid an unnecessary
    // scroll reset on touch unless another component actually moved the page.
    if (!state.nativeTouch || window.scrollX !== state.x || window.scrollY !== state.y) {
      window.scrollTo({left:state.x, top:state.y, behavior:'instant'});
    }
    if (state.lenis && !window.semboniaMobileBaseline?.lenisDisabled && state.lenis === window.semboniaLenis) {
      // Remove Lenis's stopped-state overflow before measuring the restored page.
      if (state.resume) state.lenis.start();
      state.lenis.resize();
      state.lenis.scrollTo(state.y, {immediate:true, force:true});
    }
    restore(document.documentElement, state.root.filter(([name]) => name === 'scroll-behavior'));
    if (mobileBaseline()) {
      restoring = false;
      window.dispatchEvent(new Event('sembonia:scroll-restored'));
      return;
    }
    // Fixed-body removal and scroll restoration can be reported on separate frames.
    restoreFrame = requestAnimationFrame(() => {
      restoreFrame = requestAnimationFrame(() => {
        // Refresh after fixed-body removal has completed layout. Do not restore
        // the old position again: the user may already have started scrolling.
        if (!owners.size && state.lenis === window.semboniaLenis) state.lenis?.resize();
        restoring = false;
        window.dispatchEvent(new Event('sembonia:scroll-restored'));
      });
    });
  }
  function attachLenis(lenis) {
    if (window.semboniaMobileBaseline?.lenisDisabled || !saved) return;
    saved.lenis = lenis;
    saved.resume = !lenis.isStopped;
    lenis.stop();
  }
  window.semboniaScrollLock = {acquire, release, attachLenis,
    get size() { return owners.size; },
    get disabled() { return lockDisabled(); },
    get busy() { return !lockDisabled() && (owners.size > 0 || restoring); }
  };
  window.addEventListener?.('sembonia:mobile-baseline-change', () => {
    if (lockDisabled()) {
      const wasRestoring = restoring;
      cancelAnimationFrame(restoreFrame);
      restoring = false;
      if (saved) restorePageLock();
      else if (wasRestoring) window.dispatchEvent(new Event('sembonia:scroll-restored'));
      return;
    }
    if (saved) return;
    // An overlay may still be open when the viewport leaves the mobile test.
    // Reapply the normal lock once, without retaining a stale desktop position.
    const pending = [...owners].filter(owner => owner.isConnected);
    owners.clear();
    pending.forEach(acquire);
  });
  // AJAX/theme-editor removal must not leave an orphaned page lock.
  new MutationObserver(() => {
    for (const owner of owners) if (!owner.isConnected) release(owner);
  }).observe(document.documentElement, {childList:true, subtree:true});
})();
